import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  getNotifications, 
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  clearAllNotifications,
} from "../services/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  Bell, 
  Check, 
  CheckCheck, 
  Trash2, 
  RefreshCw,
  FileText,
  AlertCircle,
  CreditCard,
  Clock
} from "lucide-react";
import { toast } from "sonner";
import { PushSettingsPanel } from "../components/PushSettingsPanel";

const NOTIFICATION_ICONS = {
  recurring_invoice_generated: RefreshCw,
  recurring_invoice_failed: AlertCircle,
  invoice_overdue: Clock,
  payment_received: CreditCard,
  quote_expiring: FileText,
  system: Bell,
};

const NOTIFICATION_COLORS = {
  recurring_invoice_generated: "text-emerald-500 bg-emerald-50",
  recurring_invoice_failed: "text-red-500 bg-red-50",
  invoice_overdue: "text-amber-500 bg-amber-50",
  payment_received: "text-blue-500 bg-blue-50",
  quote_expiring: "text-orange-500 bg-orange-50",
  system: "text-slate-500 bg-slate-50",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return date.toLocaleDateString("fr-FR");
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        getNotifications(),
        getUnreadNotificationCount(),
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.unread_count);
    } catch (err) {
      toast.error("Erreur lors du chargement des notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleMarkAsRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      window.dispatchEvent(new Event("notif-refresh"));
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      window.dispatchEvent(new Event("notif-refresh"));
      toast.success("Toutes les notifications marquées comme lues");
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Supprimer toutes les notifications lues ?")) return;
    try {
      await clearAllNotifications();
      setNotifications(prev => prev.filter(n => !n.read));
      toast.success("Notifications supprimées");
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleClick = async (notification) => {
    // Mark as read if not already
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }
    
    // Navigate if link exists
    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <div className="p-4 lg:p-6 xl:p-8 space-y-4 lg:space-y-6 animate-fadeIn" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-3">
            <Bell size={28} className="text-[#26788A]" />
            Notifications
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </h1>
          <p className="text-slate-500 mt-1 text-sm lg:text-base">
            {notifications.length} notification(s)
          </p>
        </div>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <Button
              variant="outline"
              className="border-slate-200"
              onClick={handleMarkAllAsRead}
            >
              <CheckCheck size={16} className="mr-2" />
              Tout marquer lu
            </Button>
          )}
          {notifications.some(n => n.read) && (
            <Button
              variant="outline"
              className="border-slate-200 text-red-600 hover:bg-red-50"
              onClick={handleClearAll}
            >
              <Trash2 size={16} className="mr-2" />
              Effacer lues
            </Button>
          )}
        </div>
      </div>

      {/* Réglages notifications push */}
      <PushSettingsPanel />

      {/* Notifications List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Chargement...</div>
      ) : notifications.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center">
            <Bell size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Aucune notification</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map((notification) => {
            const IconComponent = NOTIFICATION_ICONS[notification.type] || Bell;
            const colorClass = NOTIFICATION_COLORS[notification.type] || "text-slate-500 bg-slate-50";
            
            return (
              <Card 
                key={notification.id}
                data-testid={`notification-card-${notification.id}`}
                className={`bg-white border-slate-200 cursor-pointer transition-all hover:shadow-md ${
                  !notification.read ? "border-l-4 border-l-[#26788A]" : ""
                }`}
                onClick={() => handleClick(notification)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${colorClass}`}>
                      <IconComponent size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <h3 data-testid={`notification-title-${notification.id}`} className={`font-medium min-w-0 [overflow-wrap:anywhere] ${!notification.read ? "text-slate-900" : "text-slate-600"}`}>
                          {notification.title}
                        </h3>
                        <span data-testid={`notification-time-${notification.id}`} className="text-xs text-slate-400 whitespace-nowrap">
                          {formatDate(notification.created_at)}
                        </span>
                      </div>
                      <p data-testid={`notification-message-${notification.id}`} className="text-sm text-slate-500 mt-1 [overflow-wrap:anywhere]">
                        {notification.message}
                      </p>
                    </div>
                    {!notification.read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-[#26788A]"
                        data-testid={`notification-read-${notification.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                      >
                        <Check size={16} />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
